(() => {
  'use strict';
  if (window.__WHITEHOLE_CART_V60__) return;
  window.__WHITEHOLE_CART_V60__ = true;

  const root = () => window.Shopify?.routes?.root || '/';
  const routes = () => ({
    add: window.routes?.cart_add_url || `${root()}cart/add.js`,
    cart: window.routes?.cart_url || `${root()}cart`,
    cartJson: `${root()}cart.js`,
    change: window.routes?.cart_change_url || `${root()}cart/change.js`,
    checkout: `${root()}checkout`
  });

  const toast = (message, isError = false) => {
    const node = document.querySelector('[data-wh-cart-toast]');
    if (!node) return;
    node.textContent = message;
    node.classList.toggle('is-error', isError);
    node.classList.add('is-visible');
    clearTimeout(node._whiteholeTimer);
    node._whiteholeTimer = setTimeout(() => node.classList.remove('is-visible'), 3200);
  };

  const setBusy = (button, busy) => {
    if (!button) return;
    button.disabled = busy;
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
    button.classList.toggle('loading', busy);
    button.classList.toggle('is-loading', busy);
    button.querySelector('.loading__spinner')?.classList.toggle('hidden', !busy);
  };

  const updateCartCount = async () => {
    try {
      const response = await fetch(routes().cartJson, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Cart count request failed.');
      const cart = await response.json();
      const count = Number(cart.item_count || 0);

      document.querySelectorAll('[data-cart-count]').forEach((node) => {
        node.textContent = String(count);
        node.hidden = count === 0;
        node.setAttribute('aria-hidden', count === 0 ? 'true' : 'false');
      });
      document.querySelectorAll('[data-cart-open]').forEach((node) => {
        node.setAttribute('aria-label', `Open shopping cart with ${count} ${count === 1 ? 'item' : 'items'}`);
      });

      const dawnBubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
      if (dawnBubble) {
        const visibleCount = dawnBubble.querySelector('span[aria-hidden="true"]');
        if (visibleCount) visibleCount.textContent = String(count);
        dawnBubble.classList.toggle('hidden', count === 0);
        dawnBubble.hidden = count === 0;
        dawnBubble.setAttribute('aria-hidden', count === 0 ? 'true' : 'false');
      }
      return cart;
    } catch (error) {
      console.warn('WHITEHOLE cart count update failed', error);
      return null;
    }
  };

  let cartSyncTimer = 0;
  const scheduleCartSync = (delay = 120) => {
    clearTimeout(cartSyncTimer);
    cartSyncTimer = window.setTimeout(() => updateCartCount(), delay);
  };

  const hydrateDrawerFromHtml = (html) => {
    if (!html) return false;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const incomingDrawer = parsed.querySelector('cart-drawer');
    const currentDrawer = document.querySelector('cart-drawer');
    if (!incomingDrawer || !currentDrawer) return false;

    currentDrawer.innerHTML = incomingDrawer.innerHTML;
    currentDrawer.className = incomingDrawer.className;
    currentDrawer.bindOverlay?.();
    currentDrawer.setHeaderCartIconAccessibility?.();
    return true;
  };

  const fetchFreshDrawer = async () => {
    const response = await fetch(`${routes().cart}?section_id=cart-drawer&_=${Date.now()}`, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error('Fresh cart drawer request failed.');
    return hydrateDrawerFromHtml(await response.text());
  };

  const renderDrawer = async (payload, trigger) => {
    let drawer = document.querySelector('cart-drawer');
    let rendered = false;

    if (drawer && payload?.sections?.['cart-drawer']) {
      try {
        drawer.setActiveElement?.(trigger || document.activeElement);
        drawer.renderContents?.(payload);
        rendered = true;
      } catch (error) {
        console.warn('WHITEHOLE bundled cart rendering failed', error);
      }
    }

    // Fetch the drawer from /cart as a source of truth. This prevents stale UI on
    // search, collection and app-rendered pages where bundled sections can be null.
    try {
      rendered = (await fetchFreshDrawer()) || rendered;
    } catch (error) {
      console.warn('WHITEHOLE fresh cart drawer rendering failed', error);
    }

    drawer = document.querySelector('cart-drawer');
    if (rendered && drawer) {
      drawer.setActiveElement?.(trigger || document.activeElement);
      requestAnimationFrame(() => drawer.open?.(trigger));
      return true;
    }
    return false;
  };

  const formIsCartAdd = (form) => {
    if (!(form instanceof HTMLFormElement)) return false;
    if (form.dataset.whiteholeCartIgnore === 'true') return false;
    const action = form.getAttribute('action') || '';
    return (
      form.classList.contains('wh-ajax-add') ||
      form.dataset.type === 'add-to-cart-form' ||
      form.closest('product-form') ||
      /\/cart\/add(?:\.js)?(?:\?|$)/.test(action)
    );
  };

  const buildFormData = (form, submitter) => {
    let data;
    try {
      data = submitter ? new FormData(form, submitter) : new FormData(form);
    } catch (_) {
      data = new FormData(form);
      if (submitter?.name) data.append(submitter.name, submitter.value || '');
    }
    data.delete('sections');
    data.delete('sections_url');
    data.append('sections', 'cart-drawer,cart-icon-bubble');
    // Rendering in /cart context is more reliable than the current search/collection page.
    data.append('sections_url', routes().cart);
    return data;
  };

  const addFormToCart = async (form, submitter) => {
    const isBuyNow = submitter?.dataset.buyNow === 'true' || submitter?.name === 'buy_now';
    const errorNode = form.querySelector('[data-wh-cart-error], .product-form__error-message');
    setBusy(submitter, true);
    if (errorNode) {
      errorNode.textContent = '';
      errorNode.closest('[hidden]')?.removeAttribute('hidden');
      errorNode.hidden = true;
    }

    try {
      const response = await fetch(routes().add, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: buildFormData(form, submitter)
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.status) {
        throw new Error(payload.description || payload.message || 'This item could not be added to the cart.');
      }

      await updateCartCount();

      if (isBuyNow) {
        window.location.assign(routes().checkout);
        return;
      }

      const drawerOpened = await renderDrawer(payload, submitter);
      await updateCartCount();

      if (!drawerOpened) {
        // Guaranteed fallback: show the up-to-date cart immediately rather than leaving stale UI.
        window.location.assign(routes().cart);
        return;
      }

      toast('Added to your cart.');
      document.dispatchEvent(new CustomEvent('whitehole:cart-added', { detail: payload }));
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'whitehole-cart-v60',
          productVariantId: new FormData(form).get('id'),
          cartData: payload
        });
      }
    } catch (error) {
      const message = error?.message || 'This item could not be added to the cart.';
      if (errorNode) {
        errorNode.hidden = false;
        errorNode.textContent = message;
        errorNode.closest('.product-form__error-message-wrapper')?.removeAttribute('hidden');
      }
      toast(message, true);
    } finally {
      setBusy(submitter, false);
    }
  };

  // Capture phase deliberately takes ownership before legacy product-form listeners.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!formIsCartAdd(form)) return;
      const submitter = event.submitter || form.querySelector('button[type="submit"], input[type="submit"]');
      if (submitter?.dataset.whiteholeNative === 'true') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      addFormToCart(form, submitter);
    },
    true
  );

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-cart-open]');
    if (trigger) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        event.preventDefault();
        drawer.setActiveElement?.(trigger);
        drawer.open(trigger);
      }
    }

    if (event.target.closest('cart-remove-button, [data-cart-remove], .cart-remove-button, button[name="minus"], button[name="plus"]')) {
      scheduleCartSync(450);
    }
  });

  document.addEventListener('whitehole:cart-updated', () => scheduleCartSync(40));
  document.addEventListener('cart:updated', () => scheduleCartSync(40));
  window.addEventListener('pageshow', () => scheduleCartSync(0));
  window.addEventListener('focus', () => scheduleCartSync(0));
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) scheduleCartSync(0);
  });

  window.WhiteholeCart = {
    add: async (items, options = {}) => {
      const data = new FormData();
      (items || []).forEach((item, index) => {
        data.append(`items[${index}][id]`, String(item.id));
        data.append(`items[${index}][quantity]`, String(item.quantity || 1));
      });
      data.append('sections', 'cart-drawer,cart-icon-bubble');
      data.append('sections_url', routes().cart);
      const response = await fetch(routes().add, {
        method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: data
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.status) throw new Error(payload.description || payload.message || 'Could not add item.');
      await updateCartCount();
      if (options.buyNow) window.location.assign(routes().checkout);
      else await renderDrawer(payload, options.trigger);
      return payload;
    },
    toast,
    updateCartCount,
    openDrawer: (trigger) => {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer?.open) return false;
      drawer.setActiveElement?.(trigger || document.activeElement);
      drawer.open(trigger);
      return true;
    }
  };

  updateCartCount();
})();
